/**
 * Collection item view (CB-5a) — the card-vs-row builder.
 *
 * DOM-FIRST, NOT STRING-FIRST. Every user-supplied value reaches the tree
 * through `textContent` — never interpolated into an HTML string, not even an
 * escaped one. `poster.build` and any custom slot accept an HTMLElement or
 * DocumentFragment and are APPENDED, never assigned as innerHTML. Highlight
 * marks are built as nodes over a text node from `highlightRanges` index
 * pairs — never by regex-replacing into an already-escaped string (palette's
 * `highlightMatch`, the escaping hazard this layer refuses to inherit).
 *
 * Card and row differ in exactly three things: the modifier class
 * (pb-coll-card / pb-coll-row), the container (.pb-coll-grid / .pb-coll-rows),
 * and whether .pb-coll-trail is populated. Node names, data attributes, class
 * hooks and listeners are identical — which is why `layout` is a one-word
 * switch. Identity is `data-cb-id`, ALWAYS — never an index (16C2 rule).
 */

import { createButton, createCheckbox } from './components.js';
import { illustrationEl } from './illustrations.js';
import { THUMB_FAILED_ATTR } from '../shared/thumbnail-url.js';
import { highlightRanges } from '../shared/collection-filter.js';

/** Reserved data-cb-action values wired by the browser's delegated listeners. */
export const ACTION_ACTIVATE = '__activate';
export const ACTION_TOGGLE = '__toggle';

/** Fill a label element with text, marking the query's highlight ranges. */
export function paintLabel(el, text, query) {
  const value = String(text ?? '');
  el.textContent = '';
  const ranges = query ? highlightRanges(value, query) : [];
  if (!ranges.length) {
    el.textContent = value;
    return;
  }
  let at = 0;
  for (const [start, end] of ranges) {
    if (start > at) el.appendChild(document.createTextNode(value.slice(at, start)));
    const mark = document.createElement('mark');
    mark.className = 'pb-coll-mark';
    mark.textContent = value.slice(start, end);
    el.appendChild(mark);
    at = end;
  }
  if (at < value.length) el.appendChild(document.createTextNode(value.slice(at)));
}

/** Terminal thumbnail state, shared by collection surfaces without losing diagnostics. */
export function renderPosterUnavailable() {
  const holder = document.createElement('div');
  holder.className = 'pb-coll-poster-unavailable';
  holder.setAttribute('role', 'img');
  holder.setAttribute('aria-label', 'Preview unavailable');
  holder.setAttribute(THUMB_FAILED_ATTR, '1');
  const art = document.createElement('span');
  art.className = 'pb-illo';
  art.appendChild(illustrationEl('previews', { variant: 'error' }));
  const label = document.createElement('span');
  label.textContent = 'Preview unavailable';
  holder.append(art, label);
  return holder;
}

export function buildCollectionPoster(item) {
  const poster = item.poster || { kind: 'none' };
  if (poster.kind === 'none') return null;

  // Poster-IS-the-button: through the kit's `bare` variant, which carries no
  // .pb-btn surface — .pb-coll-primary owns the look.
  const host = item.primaryAction
    ? createButton({
        variant: 'bare',
        className: 'pb-coll-primary',
        ariaLabel: item.primaryAction.ariaLabel || item.primaryAction.label || '',
        dataset: { cbAction: ACTION_ACTIVATE }
      })
    : document.createElement('div');

  const box = document.createElement('div');
  box.className = 'pb-coll-poster';
  if (poster.aspect === 'wide') box.classList.add('pb-coll-poster-wide');
  // SS-6: 'strip' — a short section in a square capture (a nav bar in the top
  // ~10% of 1200x1200). The class also top-anchors the image; see
  // .pb-coll-poster-strip in collection.css.
  if (['strip', 'footer', 'short'].includes(poster.aspect)) {
    box.classList.add(`pb-coll-poster-${poster.aspect}`);
  }
  // MK-2a. Both default to today's behaviour, so no existing surface moves.
  // They apply to the BOX form only — `.pb-coll-poster-img`, the template
  // card's mechanics, is built by its surface and never reaches here.
  if (poster.fit === 'contain') box.classList.add('pb-coll-poster-contain');
  if (poster.background === 'checker') box.classList.add('pb-checker');
  if (poster.hue !== undefined) box.style.setProperty('--pb-coll-hue', String(poster.hue));
  // DS-4: the ART's own aspect, for a surface whose records vary in shape (the
  // decorative library, where a divider is 6:1 and a blob is 1:1). Same shape
  // and same reason as `hue` directly above — per-ITEM state written inline,
  // never a register value — and registered in RUNTIME_SET_TOKENS for the same
  // reason. A surface that omits it is unchanged.
  if (poster.artRatio !== undefined) {
    box.style.setProperty('--pb-coll-art-ratio', String(poster.artRatio));
  }

  if (poster.kind === 'custom' && typeof poster.build === 'function') {
    const built = poster.build(item);
    if (built) box.appendChild(built);
  } else if (poster.kind === 'monogram') {
    const mono = document.createElement('div');
    mono.className = 'pb-coll-monogram';
    mono.textContent = String(poster.monogram ?? '').slice(0, 2);
    box.appendChild(mono);
  } else {
    const img = document.createElement('img');
    img.alt = String(poster.alt ?? '');
    img.decoding = 'async';
    img.loading = 'lazy';
    if (poster.thumbKey !== undefined) img.dataset.thumbKey = String(poster.thumbKey);
    if (poster.thumbVersion !== undefined) img.dataset.thumbVersion = String(poster.thumbVersion);
    if (poster.src) {
      img.src = poster.src;
      if (poster.srcset) img.srcset = poster.srcset;
      if (poster.sizes) img.sizes = poster.sizes;
    } else {
      // The pool fills it in; hidden until pixels exist so the box (which
      // carries the aspect-ratio) shows the pulse background, not a broken img.
      img.hidden = true;
    }
    box.appendChild(img);
  }

  host.appendChild(box);
  return host;
}

/**
 * The variant, the size and the `data-cb-action` hook are the CONTRACT and are
 * not negotiable per surface — that is what makes every overlay action in the
 * app the same control. `className`, `dataset` and `testid` are pass-throughs
 * for state a surface has to own: the template picker's star is repainted from
 * a `favorites:changed` subscription that finds it by `data-pb-tpl-fav`, which
 * it cannot do if the shared builder drops the attribute.
 *
 * `cbAction` is merged LAST so a surface cannot overwrite the routing key and
 * silently detach its own button from `onAction`.
 */
function buildActionButton(action) {
  const btn = createButton({
    variant: action.danger ? 'danger' : 'ghost',
    size: 'sm',
    icon: action.icon,
    label: action.icon ? undefined : action.label,
    ariaLabel: action.label,
    tooltip: action.tooltip || action.label,
    busyLabel: action.busyLabel,
    disabled: !!action.disabled,
    className: action.className,
    testid: action.testid,
    dataset: { ...(action.dataset || {}), cbAction: action.id }
  });
  return btn;
}

/**
 * @param {object} item — the CB item contract
 * @param {{layout?: 'grid'|'rows', itemTag?: string, selectable?: boolean, query?: string,
 *   selectControl?: 'checkbox'|'radio', radioName?: string}} [opts]
 * @returns {{el: HTMLElement, titleEl: HTMLElement, metaEl: HTMLElement|null, posterImg: HTMLImageElement|null}}
 */
export function renderCollectionItem(item, opts = {}) {
  const {
    layout = 'grid',
    itemTag = 'li',
    selectable = false,
    query = '',
    selectControl = 'checkbox',
    radioName,
    // EXPLICIT, like listRole: a listbox whose children are not options is a
    // broken listbox, and no a11y suite in this repo catches that pairing.
    itemRole
  } = opts;
  const isRow = layout === 'rows';

  const el = document.createElement(itemTag);
  // `className` (CB-5b): extra surface-owned classes — the donor sheets keep
  // styling their own hooks (.pb-fleet-card) through migration, and the
  // browser harness keeps measuring them.
  el.className = [`pb-coll-item ${isRow ? 'pb-coll-row' : 'pb-coll-card'}`, item.className]
    .filter(Boolean)
    .join(' ');
  el.dataset.cbId = String(item.id);
  el.tabIndex = -1;
  if (itemRole) el.setAttribute('role', itemRole);

  if (selectable) {
    const canSelect = item.canSelect !== false;
    // CB-10: a SINGLE-select surface gets a real radio, and deliberately NOT
    // `pb-checkbox-input`. The kit's checkbox paint is a square with a tick;
    // wearing it, a radio states the wrong exclusivity — the platform dot is
    // the affordance that says "one of these", and page-selection-modal's own
    // comment called this a decision, not an oversight. Same slot class, same
    // `data-cb-action`, so positioning and the delegated listeners are shared.
    if (selectControl === 'radio') {
      const wrap = document.createElement('label');
      wrap.className = 'pb-coll-check pb-coll-check-radio';
      const input = document.createElement('input');
      input.type = 'radio';
      if (radioName) input.name = radioName;
      input.className = 'pb-coll-check-input';
      input.setAttribute('aria-label', `Select ${item.title ?? item.id}`);
      if (!canSelect) input.disabled = true;
      input.dataset.cbAction = ACTION_TOGGLE;
      wrap.appendChild(input);
      el.appendChild(wrap);
    } else {
      const check = createCheckbox({
        ariaLabel: `Select ${item.title ?? item.id}`,
        disabled: !canSelect,
        className: 'pb-coll-check',
        inputClass: 'pb-coll-check-input'
      });
      check.input.dataset.cbAction = ACTION_TOGGLE;
      el.appendChild(check.wrap);
    }
  }

  if (item.leading instanceof Node) el.appendChild(item.leading);

  const posterHost = buildCollectionPoster(item);
  if (posterHost) el.appendChild(posterHost);

  let titleEl;
  let metaEl = null;
  if (item.content instanceof Node) {
    // `content` (CB-5b): a surface-owned body, APPENDED never innerHTML'd —
    // the migration-parity form: the module renders the row shell (identity,
    // selection, leading, actions, state) and the surface keeps its exact
    // markup. Title/meta painting is then the surface's job on update.
    el.appendChild(item.content);
    titleEl = el.querySelector('[data-cb-title]');
    metaEl = el.querySelector('[data-cb-meta]');
    if (titleEl) paintLabel(titleEl, item.title, query);
    if (metaEl && item.meta !== undefined && item.meta !== null) {
      metaEl.textContent = String(item.meta);
    }
  } else {
    const text = document.createElement('div');
    text.className = 'pb-coll-text';

    titleEl = document.createElement('div');
    titleEl.className = 'pb-coll-title';
    if (item.titleAttr) titleEl.dataset.tooltip = String(item.titleAttr);
    paintLabel(titleEl, item.title, query);
    text.appendChild(titleEl);

    if (item.meta !== undefined && item.meta !== null) {
      metaEl = document.createElement('div');
      metaEl.className = 'pb-coll-meta-line';
      if (item.metaAttr) metaEl.dataset.tooltip = String(item.metaAttr);
      metaEl.textContent = String(item.meta);
      text.appendChild(metaEl);
    }

    if (Array.isArray(item.chips) && item.chips.length) {
      const chips = document.createElement('div');
      chips.className = 'pb-coll-item-chips';
      for (const chip of item.chips) {
        const span = document.createElement('span');
        span.className = 'pb-coll-badge';
        if (chip.tone) span.dataset.tone = String(chip.tone);
        if (chip.title) span.dataset.tooltip = String(chip.title);
        span.textContent = String(chip.text ?? '');
        chips.appendChild(span);
      }
      text.appendChild(chips);
    }
    el.appendChild(text);
  }

  if (item.badge?.text) {
    const badge = document.createElement('span');
    badge.className = 'pb-coll-badge pb-coll-badge-corner';
    badge.textContent = String(item.badge.text);
    el.appendChild(badge);
  }

  const actions = Array.isArray(item.actions) ? item.actions : [];
  const overlay = actions.filter((a) => (a.slot || (isRow ? 'trail' : 'overlay')) === 'overlay');
  const trail = actions.filter((a) => (a.slot || (isRow ? 'trail' : 'overlay')) === 'trail');
  if (overlay.length) {
    const wrap = document.createElement('div');
    wrap.className = 'pb-coll-actions';
    for (const a of overlay) wrap.appendChild(buildActionButton(a));
    el.appendChild(wrap);
  }
  if (trail.length) {
    const wrap = document.createElement('div');
    wrap.className = 'pb-coll-trail';
    for (const a of trail) wrap.appendChild(buildActionButton(a));
    el.appendChild(wrap);
  }

  applyItemState(el, item);
  // The pool fills the poster's <img>, or any surface-owned thumb slot marked
  // data-cb-thumb inside `leading`/`content` (fleet's 72×54 row thumb).
  return {
    el,
    titleEl,
    metaEl,
    posterImg: el.querySelector('.pb-coll-poster img, img[data-cb-thumb]')
  };
}

function applyItemState(el, item) {
  const state = item.state || {};
  el.classList.toggle('is-current', !!state.current);
  el.classList.toggle('is-danger', !!state.danger);
  el.classList.toggle('is-busy', !!state.busy);
  el.classList.toggle('is-editing', !!state.editing);
  if (state.disabled) el.setAttribute('aria-disabled', 'true');
  else el.removeAttribute('aria-disabled');
  if (el.getAttribute('role') === 'option') {
    el.setAttribute('aria-selected', String(!!state.current || !!state.selected));
  }
}

/**
 * Refresh an item's text and state IN PLACE. The poster <img> is deliberately
 * never touched, re-created or re-src'd — that is the 224ms/keystroke +
 * 5.98MB lesson, and collection-surface-tests stamps a sentinel to prove it.
 */
export function updateCollectionItem(refs, item, { query = '' } = {}) {
  if (refs.titleEl) paintLabel(refs.titleEl, item.title, query);
  if (refs.metaEl && item.meta !== undefined && item.meta !== null) {
    refs.metaEl.textContent = String(item.meta);
  }
  applyItemState(refs.el, item);
}
